import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Target, Save, Loader2, Star, Briefcase, Users, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { TopBar } from "@/components/layout/TopBar";

const SPHERE_META = {
  business:     { icon: Briefcase, label: "Business",     tone: "#0B2545" },
  professional: { icon: Users,     label: "Professionnel", tone: "#7A5B14" },
  personal:     { icon: Heart,     label: "Personnel",    tone: "#B91C1C" },
};

export default function AbiTrajectory() {
  const [trajectory, setTrajectory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/abi/copilot/trajectory");
      setTrajectory(r.data);
    } catch (e) { toast.error("Erreur de chargement."); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const updateGoal = async (key, patch) => {
    try {
      const r = await api.patch("/abi/copilot/trajectory/goal",
                                  { key, ...patch });
      setTrajectory(r.data);
    } catch (e) { toast.error("Erreur"); }
  };

  const setMission = async (mission_fr, north_star) => {
    setSaving(true);
    try {
      const r = await api.put("/abi/copilot/trajectory",
                                { mission_fr, north_star });
      setTrajectory(r.data);
      toast.success("Trajectoire enregistrée.");
    } catch (e) { toast.error("Erreur"); }
    finally { setSaving(false); }
  };

  if (loading || !trajectory) return (
    <div className="p-8 flex items-center gap-2"><Loader2 size={16} className="animate-spin" /> Chargement…</div>
  );

  const bySphere = {};
  for (const g of trajectory.goals || []) {
    bySphere[g.sphere] = bySphere[g.sphere] || [];
    bySphere[g.sphere].push(g);
  }

  const progress = trajectory.progress || {};

  return (
    <div data-testid="abi-trajectory-root">
      <TopBar
        title="Ma trajectoire de réussite"
        subtitle="Définissez vos objectifs — le Copilote les utilise pour évaluer chaque action."
      />

      <div className="px-6 md:px-10 py-8 md:py-10 max-w-[1000px] mx-auto space-y-8">
        {/* Mission + North star */}
        <div className="rounded-2xl p-6" style={{ background: "linear-gradient(135deg,#0B2545 0%,#061630 100%)", color:"#F5F0E6" }}>
          <div className="text-[10px] uppercase tracking-widest opacity-70">Ma mission</div>
          <textarea
            defaultValue={trajectory.mission_fr || ""}
            placeholder="Ex : Devenir la référence CSF à Lévis en 24 mois."
            className="w-full mt-2 bg-transparent border-b border-white/20 pb-2 text-lg font-serif focus:outline-none"
            style={{ color: "#F5F0E6" }}
            onBlur={(e) => setMission(e.target.value, trajectory.north_star)}
            data-testid="abi-trajectory-mission"
          />
          <div className="mt-4 text-[10px] uppercase tracking-widest opacity-70">
            Progression globale · {progress.declared_count || 0}/{progress.total_goals || 16} objectifs déclarés
          </div>
          <div className="mt-1 flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full bg-white/10">
              <div className="h-full rounded-full transition-all"
                    style={{ width: `${progress.completion_pct || 0}%`, background: "#C5A880" }} />
            </div>
            <div className="font-serif text-2xl font-bold" style={{ color: "#C5A880" }}>
              {progress.completion_pct || 0}%
            </div>
          </div>
        </div>

        {/* Goals by sphere */}
        {["business", "professional", "personal"].map((sphere) => {
          const meta = SPHERE_META[sphere];
          const Icon = meta.icon;
          const goals = bySphere[sphere] || [];
          return (
            <section key={sphere}>
              <div className="flex items-center gap-2 mb-3">
                <Icon size={18} style={{ color: meta.tone }} />
                <h2 className="font-serif text-xl font-bold" style={{ color: meta.tone }}>
                  {meta.label}
                </h2>
              </div>
              <div className="space-y-2">
                {goals.map((g) => {
                  const isNorthStar = trajectory.north_star === g.key;
                  return (
                    <div key={g.key}
                          className="bg-white border rounded-lg p-3 flex items-center gap-3"
                          style={{ borderColor: g.declared ? "#C5A880" : "#E5E7EB",
                                    borderLeftWidth: g.declared ? 4 : 1 }}
                          data-testid={`abi-trajectory-goal-${g.key}`}>
                      <button
                        type="button"
                        onClick={() => setMission(trajectory.mission_fr, g.key)}
                        title="Marquer comme North Star"
                        data-testid={`abi-trajectory-star-${g.key}`}
                      >
                        <Star size={16}
                               fill={isNorthStar ? "#C5A880" : "transparent"}
                               style={{ color: isNorthStar ? "#7A5B14" : "#94A3B8" }} />
                      </button>
                      <div className="flex-1">
                        <div className="text-sm font-medium" style={{ color: "#0B2545" }}>
                          {g.label_fr}
                        </div>
                        <div className="text-[11px] text-slate-500">
                          {g.frequency} · {g.unit}
                        </div>
                      </div>
                      <input
                        type="number"
                        min="0"
                        defaultValue={g.target_value || ""}
                        placeholder="Cible"
                        className="w-20 px-2 py-1 border rounded text-sm text-right"
                        style={{ borderColor: "#E5E7EB" }}
                        onBlur={(e) => {
                          const v = parseFloat(e.target.value) || 0;
                          updateGoal(g.key, {
                            target_value: v,
                            declared: v > 0,
                          });
                        }}
                        data-testid={`abi-trajectory-target-${g.key}`}
                      />
                      <input
                        type="number"
                        min="0"
                        defaultValue={g.current_value || ""}
                        placeholder="Actuel"
                        className="w-20 px-2 py-1 border rounded text-sm text-right"
                        style={{ borderColor: "#E5E7EB" }}
                        onBlur={(e) => {
                          updateGoal(g.key, { current_value: parseFloat(e.target.value) || 0 });
                        }}
                        data-testid={`abi-trajectory-current-${g.key}`}
                      />
                      <select
                        defaultValue={g.priority || 3}
                        onChange={(e) => updateGoal(g.key, { priority: parseInt(e.target.value) })}
                        className="border rounded text-xs px-1 py-1"
                        style={{ borderColor: "#E5E7EB" }}
                      >
                        {[1,2,3,4,5].map(p => <option key={p} value={p}>P{p}</option>)}
                      </select>
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}

        <p className="text-xs italic text-slate-500">
          Astuce · plus vous déclarez d'objectifs (cible > 0), plus le Copilote peut évaluer si chaque action vous rapproche vraiment de votre trajectoire.
        </p>
      </div>
    </div>
  );
}
